// 創建 src/services/api/companyService.ts

import { supabase } from '../supabase/client';
import {
  isValidTaiwanGuiNumber,
  normalizeCompanyTaxIdForDisplay,
  normalizeCompanyTaxIdForSave,
  normalizeOptionalCompanyTextField,
} from '../../utils/taiwanGuiNumber';
import {
  companyNeedsRegProfileSync,
  parseCompanyRegProfile,
  regProfileServiceAreas,
  regProfileSpecialtyIds,
} from '../../utils/companyRegProfile';
import { resolveProfileImageUrl, DEFAULT_COMPANY_IMAGE_PATH } from '../../utils/profileImageUrl';
import { isCompanyEligibleForHome, isLowQualityDisplayName } from '../../utils/homeRecommendedProviders';
import { isUsableProviderLocation, isUsableUserGeoLocation } from '../../utils/geoDefaults';
import { syncCompanyPremiumExpiryStrict } from '../premium/companyPremiumExpirySync';
import { isCompanyPremiumActive } from '../../utils/companyPremium';
import {
  getPremiumPlanDurationDays,
  getPremiumPlanPrice,
  inferPremiumPlanFromDaysRemaining,
} from '../../config/premiumPlans';

// 檢查是否為有效的 UUID
const isValidUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

export interface Company {
  id: string;
  userId: string;
  companyName: string;
  taxId: string;
  businessLicense: string;
  description: string;
  address: string;
  phone: string;
  website: string;
  employeeCount: number;
  establishedDate: string;
  industry: string;
  companySize: string;
  annualRevenue: number;
  status: string;
  rating: number;
  totalReviews: number;
  totalEarnings: number;
  completedJobs: number;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
  location?: { lat: number; lng: number }; // 地理位置座標
  services?: string[]; // 提供的服務列表
  teamSize?: number; // 團隊大小
  email?: string; // 聯絡郵箱
  profileImage?: string; // 公司頭像
  specialties?: number[]; // 專業技能
  serviceAreas?: string[]; // 服務區域
  hourlyRate?: number; // 時薪
  certificates?: string[]; // 證書
  bankAccount?: { // 銀行帳戶信息
    bankCode: string;
    accountNumber: string;
    accountName: string;
  };
  accountBalance?: number; // 帳戶餘額
  isPremium?: boolean; // 是否為高級會員
  premiumUntil?: string; // 高級會員到期時間
  premiumPlanType?: 'monthly' | 'quarterly' | 'yearly'; // Premium 方案類型
  preferredFlow?: string | null;
  /** 後台人工核對統編／營業登記（與 users.is_verified 個人身分不同） */
  manualBusinessRegistrationVerifiedAt?: string | null;
  manualBusinessRegistrationVerifiedBy?: string | null;
  manualBusinessRegistrationNotes?: string | null;
}

export class CompanyService {
  /**
   * 獲取公司當前的 Premium 方案類型
   */
  static async getCurrentPremiumPlanType(companyId: string): Promise<'monthly' | 'quarterly' | 'yearly' | undefined> {
    try {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('premium_subscriptions')
        .select('plan_type')
        .eq('company_id', companyId)
        .eq('status', 'active')
        .gte('expires_at', now)
        .order('started_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error || !data) {
        return undefined;
      }

      return data.plan_type as 'monthly' | 'quarterly' | 'yearly';
    } catch (error) {
      console.error('獲取 Premium 方案類型失敗:', error);
      return undefined;
    }
  }

  /**
   * 登入後若 companies 缺 specialties / service_areas，從 auth user_metadata.reg_profile 補寫
   */
  static async syncCompanyFromRegProfileIfEmpty(
    companyId: string,
    row: {
      specialties?: number[] | null;
      service_areas?: string[] | null;
      address?: string | null;
      description?: string | null;
    }
  ): Promise<{
    specialties: number[];
    service_areas: string[];
    address?: string | null;
    description?: string | null;
  } | null> {
    if (!companyNeedsRegProfileSync(row)) return null;

    const { data: authData, error: authError } = await supabase.auth.getUser();
    if (authError || !authData.user) return null;

    const profile = parseCompanyRegProfile(authData.user.user_metadata as Record<string, unknown>);
    if (!profile) return null;

    const specsFromReg = regProfileSpecialtyIds(profile);
    const areasFromReg = regProfileServiceAreas(profile);
    const updatePayload: Record<string, unknown> = {};

    if ((row.specialties ?? []).length === 0 && specsFromReg.length > 0) {
      updatePayload.specialties = specsFromReg;
    }
    if ((row.service_areas ?? []).length === 0 && areasFromReg.length > 0) {
      updatePayload.service_areas = areasFromReg;
    }
    if (!row.address?.trim() && profile.address?.trim()) {
      updatePayload.address = profile.address.trim();
    }
    if (!row.description?.trim() && profile.description?.trim()) {
      updatePayload.description = profile.description.trim();
    }

    if (Object.keys(updatePayload).length === 0) return null;

    updatePayload.updated_at = new Date().toISOString();
    const { error: updateError } = await supabase
      .from('companies')
      .update(updatePayload)
      .eq('id', companyId);

    if (updateError) {
      console.warn('syncCompanyFromRegProfileIfEmpty failed:', updateError);
      return null;
    }

    console.log('✅ 已從 reg_profile 補寫公司 specialties / service_areas', companyId);
    const mergedSpecs =
      (updatePayload.specialties as number[] | undefined) ??
      (row.specialties?.length ? row.specialties : specsFromReg);
    const mergedAreas =
      (updatePayload.service_areas as string[] | undefined) ??
      (row.service_areas?.length ? row.service_areas : areasFromReg);

    return {
      specialties: mergedSpecs ?? [],
      service_areas: mergedAreas ?? [],
      address: (updatePayload.address as string | undefined) ?? row.address,
      description: (updatePayload.description as string | undefined) ?? row.description,
    };
  }

  // 根據用戶ID獲取公司資料
  static async getCompanyByUserId(userId: string): Promise<Company | null> {
    try {
      console.log('獲取公司資料，用戶ID:', userId);

      // 如果 userId 不是有效的 UUID（例如 temp-user-id），跳過查詢
      if (!isValidUUID(userId)) {
        console.log('用戶ID不是有效的UUID，跳過查詢');
        return null;
      }

      // 勿使用 users!inner：users 表 RLS 若擋住嵌入查詢，會讓整筆 companies 讀不到，
      // 前端誤判「無公司」而 insert，觸發 user_id 唯一鍵 409。
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      console.log('查詢結果:', { companyData, companyError });

      if (companyError) {
        console.error('獲取公司資料失敗:', companyError);
        return null;
      }

      if (!companyData) {
        console.log('沒有找到公司資料');
        return null;
      }

      const synced = await CompanyService.syncCompanyFromRegProfileIfEmpty(companyData.id, {
        specialties: companyData.specialties,
        service_areas: companyData.service_areas,
        address: companyData.address,
        description: companyData.description,
      });
      if (synced) {
        if (synced.specialties.length > 0) companyData.specialties = synced.specialties;
        if (synced.service_areas.length > 0) companyData.service_areas = synced.service_areas;
        if (synced.address) companyData.address = synced.address;
        if (synced.description) companyData.description = synced.description;
      }

      const expirySync = await syncCompanyPremiumExpiryStrict(companyData.id);
      if (expirySync.synced) {
        companyData.is_premium = false;
      }

      let userData: { name?: string; email?: string; phone?: string; profile_image?: string; is_verified?: boolean } | null =
        null;
      if (companyData.user_id) {
        const { data: user, error: userError } = await supabase
          .from('users')
          .select('name, email, phone, profile_image, is_verified')
          .eq('id', companyData.user_id)
          .maybeSingle();
        if (!userError && user) {
          userData = user;
        }
      }

      console.log('公司資料:', companyData);

      return {
        id: companyData.id,
        userId: companyData.user_id,
        companyName: companyData.company_name || '',
        taxId: normalizeCompanyTaxIdForDisplay(companyData.tax_id),
        businessLicense: companyData.business_license?.trim() || '',
        description: companyData.description || '',
        address: companyData.address || '',
        phone: companyData.phone || userData?.phone || '',
        website: companyData.website || '',
        employeeCount: companyData.employee_count || 0,
        establishedDate: companyData.established_date || '',
        industry: companyData.industry || '',
        companySize: companyData.company_size || '',
        annualRevenue: companyData.annual_revenue || 0,
        status: companyData.status || 'active',
        rating: companyData.rating || 0,
        totalReviews: companyData.total_reviews || 0,
        totalEarnings: companyData.total_earnings || 0,
        completedJobs: companyData.completed_jobs || 0,
        isVerified: userData?.is_verified || (companyData as any).is_verified || false,
        createdAt: companyData.created_at,
        updatedAt: companyData.updated_at,
        preferredFlow: companyData.preferred_flow || null,
        specialties: companyData.specialties || [],
        serviceAreas: companyData.service_areas || [],
        hourlyRate: companyData.hourly_rate || 0,
        certificates: companyData.certificates || [],
        location: companyData.location || undefined,
        bankAccount: companyData.bank_account || undefined,
        accountBalance: companyData.account_balance || 0,
        isPremium: companyData.is_premium || false,
        premiumUntil: companyData.premium_until || undefined,
        premiumPlanType: await this.getCurrentPremiumPlanType(companyData.id),
        email: userData?.email || '',
        profileImage: resolveProfileImageUrl(
          companyData.profile_image || userData?.profile_image || undefined,
          { fallback: DEFAULT_COMPANY_IMAGE_PATH }
        ),
        manualBusinessRegistrationVerifiedAt:
          companyData.manual_business_registration_verified_at ?? null,
        manualBusinessRegistrationVerifiedBy:
          companyData.manual_business_registration_verified_by ?? null,
        manualBusinessRegistrationNotes:
          companyData.manual_business_registration_notes ?? null,
      };
    } catch (error) {
      console.error('獲取公司資料時發生錯誤:', error);
      return null;
    }
  }

  // 根據公司ID獲取公司資料（公開訪問）
  static async getCompanyById(companyId: string): Promise<Company | null> {
    try {
      console.log('獲取公司資料，公司ID:', companyId);

      // 步驟1: 查詢公司表
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();

      console.log('公司數據查詢結果:', { companyData, companyError });

      if (companyError) {
        // 如果是 PGRST116 錯誤（0 rows），說明公司不存在或 RLS 策略阻止訪問
        if (companyError.code === 'PGRST116') {
          console.warn('公司不存在或無法訪問:', companyId);
          console.warn('錯誤詳情:', companyError);
        } else {
          console.error('獲取公司資料失敗:', companyError);
        }
        return null;
      }

      if (!companyData) {
        console.warn('公司資料為空，ID:', companyId);
        return null;
      }

      const expirySync = await syncCompanyPremiumExpiryStrict(companyId);
      if (expirySync.synced) {
        companyData.is_premium = false;
      }

      // 步驟2: 查詢對應的用戶信息（如果 user_id 存在）
      let userData: any = null;
      if (companyData.user_id) {
        const { data: user, error: userError } = await supabase
          .from('users')
          .select('name, email, phone, profile_image, is_verified')
          .eq('id', companyData.user_id)
          .single();

        console.log('用戶數據查詢結果:', { user, userError });

        // 如果用戶查詢失敗，不影響公司資料返回（允許部分數據）
        if (!userError && user) {
          userData = user;
        }
      }

      // 步驟3: 返回合併後的數據
      return {
        id: companyData.id,
        userId: companyData.user_id,
        companyName: companyData.company_name || '',
        taxId: normalizeCompanyTaxIdForDisplay(companyData.tax_id),
        businessLicense: companyData.business_license?.trim() || '',
        description: companyData.description || '',
        address: companyData.address || '',
        phone: companyData.phone || '',
        website: companyData.website || '',
        employeeCount: companyData.employee_count || 0,
        establishedDate: companyData.established_date || '',
        industry: companyData.industry || '',
        companySize: companyData.company_size || '',
        annualRevenue: companyData.annual_revenue || 0,
        status: companyData.status || 'active',
        rating: companyData.rating || 0,
        totalReviews: companyData.total_reviews || 0,
        totalEarnings: companyData.total_earnings || 0,
        completedJobs: companyData.completed_jobs || 0,
        isVerified: userData?.is_verified || companyData.is_verified || false,
        createdAt: companyData.created_at,
        updatedAt: companyData.updated_at,
        preferredFlow: companyData.preferred_flow || null,
        specialties: companyData.specialties || [],
        serviceAreas: companyData.service_areas || [],
        hourlyRate: companyData.hourly_rate || 0,
        certificates: companyData.certificates || [],
        location: companyData.location || undefined,
        bankAccount: companyData.bank_account || undefined,
        accountBalance: companyData.account_balance || 0,
        isPremium: companyData.is_premium || false,
        premiumUntil: companyData.premium_until || undefined,
        premiumPlanType: await this.getCurrentPremiumPlanType(companyId),
        email: userData?.email || '',
        profileImage: resolveProfileImageUrl(
          companyData.profile_image || userData?.profile_image || undefined,
          { fallback: DEFAULT_COMPANY_IMAGE_PATH }
        ),
        manualBusinessRegistrationVerifiedAt:
          companyData.manual_business_registration_verified_at ?? null,
        manualBusinessRegistrationVerifiedBy:
          companyData.manual_business_registration_verified_by ?? null,
        manualBusinessRegistrationNotes:
          companyData.manual_business_registration_notes ?? null,
      };
    } catch (error) {
      console.error('獲取公司資料時發生錯誤:', error);
      return null;
    }
  }

  /** 台灣統一編號檢查碼（供表單共用） */
  static isValidTaiwanTaxId(value: string): boolean {
    return isValidTaiwanGuiNumber(value);
  }

  /**
   * 管理員：標記或清除「統編／營業登記」人工核對（RLS：admin）
   */
  static async setManualBusinessRegistrationVerification(params: {
    companyId: string;
    adminUserId: string;
    verified: boolean;
    notes?: string;
  }): Promise<{ success: boolean; error: string | null }> {
    try {
      if (params.verified) {
        const { error } = await supabase
          .from('companies')
          .update({
            manual_business_registration_verified_at: new Date().toISOString(),
            manual_business_registration_verified_by: params.adminUserId,
            manual_business_registration_notes: params.notes?.trim() || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', params.companyId);

        if (error) {
          return { success: false, error: error.message };
        }
      } else {
        const { error } = await supabase
          .from('companies')
          .update({
            manual_business_registration_verified_at: null,
            manual_business_registration_verified_by: null,
            manual_business_registration_notes: null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', params.companyId);

        if (error) {
          return { success: false, error: error.message };
        }
      }
      return { success: true, error: null };
    } catch (e: any) {
      return { success: false, error: e?.message || '更新失敗' };
    }
  }

  /**
   * 管理員：公司列表（人工核對統編／執照用）
   */
  static async listCompaniesForAdminVerification(params?: {
    limit?: number;
  }): Promise<{ companies: Company[]; error: string | null }> {
    const limit = Math.min(Math.max(params?.limit ?? 200, 1), 500);
    try {
      const { data, error } = await supabase
        .from('companies')
        .select(
          `
          *,
          users!user_id(id, name, email, phone, is_verified, profile_image)
        `
        )
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        return { companies: [], error: error.message };
      }

      const rows = data || [];
      const companies: Company[] = await Promise.all(
        rows.map(async (row: any) => ({
          id: row.id,
          userId: row.user_id,
          companyName: row.company_name || '',
          taxId: normalizeCompanyTaxIdForDisplay(row.tax_id),
          businessLicense: row.business_license || '',
          description: row.description || '',
          address: row.address || '',
          phone: row.phone || row.users?.phone || '',
          website: row.website || '',
          employeeCount: row.employee_count || 0,
          establishedDate: row.established_date || '',
          industry: row.industry || '',
          companySize: row.company_size || '',
          annualRevenue: row.annual_revenue || 0,
          status: row.status || 'active',
          rating: row.rating || 0,
          totalReviews: row.total_reviews || 0,
          totalEarnings: row.total_earnings || 0,
          completedJobs: row.completed_jobs || 0,
          isVerified: row.users?.is_verified || row.is_verified || false,
          createdAt: row.created_at,
          updatedAt: row.updated_at,
          preferredFlow: row.preferred_flow || null,
          specialties: row.specialties || [],
          serviceAreas: row.service_areas || [],
          hourlyRate: row.hourly_rate || 0,
          certificates: row.certificates || [],
          location: row.location || undefined,
          bankAccount: row.bank_account || undefined,
          accountBalance: row.account_balance || 0,
          isPremium: row.is_premium || false,
          premiumUntil: row.premium_until || undefined,
          premiumPlanType: await this.getCurrentPremiumPlanType(row.id),
          email: row.users?.email || '',
          profileImage: resolveProfileImageUrl(
            row.profile_image || row.users?.profile_image || undefined,
            { fallback: DEFAULT_COMPANY_IMAGE_PATH }
          ),
          manualBusinessRegistrationVerifiedAt:
            row.manual_business_registration_verified_at ?? null,
          manualBusinessRegistrationVerifiedBy:
            row.manual_business_registration_verified_by ?? null,
          manualBusinessRegistrationNotes:
            row.manual_business_registration_notes ?? null,
        }))
      );

      return { companies, error: null };
    } catch (e: any) {
      return { companies: [], error: e?.message || '載入失敗' };
    }
  }

  // 創建公司資料
  static async createCompany(userId: string, companyData: Partial<Company>): Promise<{ success: boolean; error: string | null }> {
    try {
      console.log('創建公司資料:', userId, companyData);

      // 如果 userId 不是有效的 UUID（例如 temp-user-id），跳過創建
      if (!isValidUUID(userId)) {
        console.log('用戶ID不是有效的UUID，跳過創建');
        return { success: false, error: '無法為臨時用戶創建公司資料，請使用真實帳號登入' };
      }

      const { error } = await supabase
        .from('companies')
        .insert({
          user_id: userId,
          company_name: companyData.companyName || '',
          tax_id: normalizeCompanyTaxIdForSave(companyData.taxId),
          business_license: normalizeOptionalCompanyTextField(companyData.businessLicense),
          description: companyData.description || '',
          address: companyData.address || '',
          phone: companyData.phone || '',
          website: normalizeOptionalCompanyTextField(companyData.website),
          employee_count: companyData.employeeCount || 0,
          established_date: companyData.establishedDate || null,
          industry: companyData.industry || '',
          company_size: companyData.companySize || '',
          annual_revenue: companyData.annualRevenue || 0,
          status: 'active',
          profile_image: companyData.profileImage || null,
          preferred_flow: companyData.preferredFlow || null
        });

      if (error) {
        console.error('創建公司資料失敗:', error);
        return { success: false, error: '創建公司資料失敗' };
      }

      console.log('公司資料創建成功');
      return { success: true, error: null };
    } catch (error) {
      console.error('創建公司資料時發生錯誤:', error);
      return { success: false, error: '創建公司資料時發生錯誤' };
    }
  }

  // 更新公司資料
  static async updateCompany(companyId: string, companyData: Partial<Company>): Promise<{ success: boolean; error: string | null }> {
    try {
      console.log('更新公司資料:', companyId, companyData);

      // 構建更新對象，只包含有值的欄位
      const updateData: any = {
          company_name: companyData.companyName,
          tax_id:
            companyData.taxId !== undefined
              ? normalizeCompanyTaxIdForSave(companyData.taxId)
              : undefined,
          business_license:
            companyData.businessLicense !== undefined
              ? normalizeOptionalCompanyTextField(companyData.businessLicense)
              : undefined,
          description: companyData.description,
          address: companyData.address,
          phone: companyData.phone,
          website:
            companyData.website !== undefined
              ? normalizeOptionalCompanyTextField(companyData.website)
              : undefined,
          employee_count: companyData.employeeCount,
          established_date: companyData.establishedDate,
          industry: companyData.industry,
          company_size: companyData.companySize,
          annual_revenue: companyData.annualRevenue,
          updated_at: new Date().toISOString()
      };

      // ✅ 如果更新了 address，同时 geocode 更新 location
      if (companyData.address !== undefined && companyData.address.trim()) {
        try {
          const { AddressService } = await import('./addressService');
          const geocodeResult = await AddressService.geocodeAddress(companyData.address);
          if (geocodeResult) {
            updateData.location = { lat: geocodeResult.lat, lng: geocodeResult.lng };
            console.log('✅ 公司地址已 geocode，更新 location:', updateData.location);
          } else {
            console.warn('⚠️ 公司地址 geocode 失败，保持原有 location');
          }
        } catch (error) {
          console.error('公司地址地理编码失败:', error);
          // 即使 geocode 失败，也继续更新其他字段
        }
      }

      // 添加所有存在的欄位（遷移後所有欄位都已存在）
      if (companyData.specialties !== undefined) {
        updateData.specialties = companyData.specialties;
      }
      if (companyData.serviceAreas !== undefined) {
        updateData.service_areas = companyData.serviceAreas;
      }
      if (companyData.hourlyRate !== undefined) {
        updateData.hourly_rate = companyData.hourlyRate;
      }
      if (companyData.bankAccount !== undefined) {
        updateData.bank_account = companyData.bankAccount;
      }
      if (companyData.certificates !== undefined && companyData.certificates !== null) {
        updateData.certificates = companyData.certificates;
      }
      if (companyData.preferredFlow !== undefined) {
        updateData.preferred_flow = companyData.preferredFlow;
      }
      if (companyData.profileImage !== undefined) {
        updateData.profile_image = companyData.profileImage;
      }

      const { error } = await supabase
        .from('companies')
        .update(updateData)
        .eq('id', companyId);

      if (error) {
        console.error('更新公司資料失敗:', error);
        return { success: false, error: '更新公司資料失敗' };
      }

      console.log('公司資料更新成功');
      return { success: true, error: null };
    } catch (error) {
      console.error('更新公司資料時發生錯誤:', error);
      return { success: false, error: '更新公司資料時發生錯誤' };
    }
  }

  // 獲取所有可用的公司列表（供客戶選擇）
  static async getAvailableCompanies(): Promise<{
    companies: Company[];
    total: number;
    error?: string;
  }> {
    try {
      console.log('獲取可用公司列表');

      // ✅ 改為 left join users；Premium 改為第二支查詢批次載入，避免 embed 關聯／RLS 造成整包 companies 查詢失敗
      // ✅ 包含 'active' 和 'pending' 狀態的公司
      const { data: companiesData, error } = await supabase
        .from('companies')
        .select(`
          *,
          users!companies_user_id_fkey(id, name, email, phone, profile_image)
        `)
        .in('status', ['active', 'pending']) // ✅ 改為包含 active 和 pending 狀態
        .order('rating', { ascending: false });

      if (error) {
        console.error('獲取公司列表失敗:', error);
        console.error('錯誤詳情:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        // ✅ 檢查是否是網絡錯誤
        if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
          return { 
            companies: [], 
            total: 0,
            error: '網絡連接失敗，請檢查網絡連接後重試'
          };
        }
        return { 
          companies: [], 
          total: 0,
          error: error.message || '獲取公司列表失敗'
        };
      }

      console.log('獲取到的公司數據:', companiesData);
      console.log('公司總數:', companiesData?.length || 0);

      // ✅ 批量获取所有公司的评价数据（动态计算真实评价）
      const companyIds = companiesData?.map((c: any) => c.id) || [];

      /** company_id → 有效中的訂閱列（避免主查詢 embed premium_subscriptions） */
      const premiumRowsByCompany = new Map<
        string,
        { plan_type: string; status: string; expires_at: string; started_at: string }[]
      >();
      if (companyIds.length > 0) {
        const nowIso = new Date().toISOString();
        const { data: premiumRows, error: premiumErr } = await supabase
          .from('premium_subscriptions')
          .select('company_id, plan_type, status, expires_at, started_at')
          .in('company_id', companyIds)
          .eq('status', 'active')
          .gt('expires_at', nowIso);
        if (premiumErr) {
          console.warn('⚠️ 批次載入 Premium 訂閱失敗（略過徽章／排序加權）:', premiumErr);
        } else if (premiumRows) {
          premiumRows.forEach((row: any) => {
            const cid = row.company_id as string;
            if (!cid) return;
            const list = premiumRowsByCompany.get(cid) || [];
            list.push(row);
            premiumRowsByCompany.set(cid, list);
          });
        }
      }
      let companyReviewsMap = new Map<string, { total: number; sum: number }>();
      
      if (companyIds.length > 0) {
        try {
          const { data: reviewsData, error: reviewsError } = await supabase
            .from('reviews')
            .select('company_id, rating')
            .in('company_id', companyIds);

          if (!reviewsError && reviewsData) {
            // 计算每个公司的评价统计
            reviewsData.forEach((review: any) => {
              const companyId = review.company_id;
              if (!companyReviewsMap.has(companyId)) {
                companyReviewsMap.set(companyId, { total: 0, sum: 0 });
              }
              const stats = companyReviewsMap.get(companyId)!;
              stats.total++;
              stats.sum += review.rating || 0;
            });
            console.log(`✅ 成功获取 ${reviewsData.length} 条评价数据，涉及 ${companyReviewsMap.size} 个公司`);
          } else if (reviewsError) {
            console.warn('⚠️ 获取评价数据失败:', reviewsError);
          }
        } catch (error) {
          console.error('获取评价数据时发生错误:', error);
        }
      }

      // ✅ 批量获取所有公司的员工数量（动态计算真实员工数）
      let companyEmployeesMap = new Map<string, number>();
      
      if (companyIds.length > 0) {
        try {
          // ✅ 修复：添加调试日志，并确保查询所有状态的员工
          const { data: employeesData, error: employeesError } = await supabase
            .from('employees')
            .select('company_id, status') // ✅ 同时选择 status 用于调试
            .in('company_id', companyIds);
            // 注意：不添加 .eq('status', 'active')，因为我们要统计所有员工

          if (!employeesError && employeesData) {
            console.log(`📊 批量查询员工数据: 总数=${employeesData.length}, 涉及公司=${companyIds.length}`);
            
            // 计算每个公司的员工数量
            employeesData.forEach((employee: any) => {
              const companyId = employee.company_id;
              companyEmployeesMap.set(companyId, (companyEmployeesMap.get(companyId) || 0) + 1);
            });
            
            // ✅ 添加调试日志：显示每个公司的员工数量
            companyIds.forEach(companyId => {
              const count = companyEmployeesMap.get(companyId) || 0;
              console.log(`  - 公司 ${companyId}: ${count} 位员工`);
            });
            
            console.log(`✅ 成功获取 ${employeesData.length} 条员工数据，涉及 ${companyEmployeesMap.size} 个公司`);
          } else if (employeesError) {
            console.warn('⚠️ 获取员工数据失败:', employeesError);
            console.warn('⚠️ 错误详情:', {
              code: employeesError.code,
              message: employeesError.message,
              details: employeesError.details
            });
          }
        } catch (error) {
          console.error('获取员工数据时发生错误:', error);
        }
      }

      // ✅ 批量統計各公司已完成工單數（與 getCompanyStats 一致：service_requests.status = completed）
      let companyCompletedJobsMap = new Map<string, number>();
      let completedJobsFromRequestsOk = false;

      if (companyIds.length > 0) {
        try {
          const { data: completedRows, error: completedJobsError } = await supabase
            .from('service_requests')
            .select('company_id')
            .in('company_id', companyIds)
            .eq('status', 'completed');

          if (!completedJobsError && completedRows) {
            completedJobsFromRequestsOk = true;
            completedRows.forEach((row: { company_id?: string | null }) => {
              const cid = row.company_id;
              if (!cid) return;
              companyCompletedJobsMap.set(cid, (companyCompletedJobsMap.get(cid) || 0) + 1);
            });
            console.log(
              `✅ 批量統計完成案件: ${completedRows.length} 筆 completed 工單，涉及 ${companyCompletedJobsMap.size} 家公司`
            );
          } else if (completedJobsError) {
            console.warn('⚠️ 獲取完成案件統計失敗:', completedJobsError);
          }
        } catch (error) {
          console.error('獲取完成案件統計時發生錯誤:', error);
        }
      }

      const companies = companiesData?.map(company => {
        // ✅ 如果 users 表中沒有記錄，嘗試從其他地方獲取 email
        let email = company.users?.email || '';
        
        // 如果沒有 email，記錄警告（但不阻止公司顯示）
        if (!email && company.user_id) {
          console.warn(`⚠️ 公司 ${company.company_name} (ID: ${company.id}) 的 users 表中沒有 email 記錄`);
        }
        
        // ✅ 獲取 Premium 訂閱資訊（來自批次查詢）
        const premiumList = premiumRowsByCompany.get(company.id) || [];
        const activeSubscription = premiumList.find(
          (sub: any) => sub.status === 'active' && new Date(sub.expires_at) > new Date()
        );
        
        const isPremium = company.is_premium && company.premium_until && new Date(company.premium_until) > new Date();
        const premiumPlanType = activeSubscription?.plan_type as 'monthly' | 'quarterly' | 'yearly' | undefined;
        
        // Premium 数据映射完成
        
        return {
        id: company.id,
        userId: company.user_id,
        companyName: company.company_name,
        taxId: normalizeCompanyTaxIdForDisplay(company.tax_id),
        businessLicense: company.business_license?.trim() || '',
        description: company.description || '專業維修服務公司',
        address: company.address,
        phone: company.phone || company.users?.phone,
        website: company.website,
        employeeCount: company.employee_count || 0,
        establishedDate: company.established_date,
        industry: company.industry || '維修服務',
        companySize: company.company_size || '中小型',
        annualRevenue: company.annual_revenue || 0,
        status: company.status,
        // ✅ 使用动态计算的真实评价数据，而不是数据库中的静态值
        rating: (() => {
          const reviewStats = companyReviewsMap.get(company.id);
          if (reviewStats && reviewStats.total > 0) {
            return Math.round((reviewStats.sum / reviewStats.total) * 10) / 10;
          }
          return 0; // 如果没有评价，返回 0 而不是默认值 4.5
        })(),
        totalReviews: companyReviewsMap.get(company.id)?.total || 0,
        totalEarnings: company.total_earnings || 0,
        completedJobs: completedJobsFromRequestsOk
          ? companyCompletedJobsMap.get(company.id) ?? 0
          : company.completed_jobs || 0,
        isVerified: company.is_verified || false,
        createdAt: company.created_at,
        updatedAt: company.updated_at,
        hourlyRate: company.hourly_rate || 0,
        specialties: company.specialties || [],
        serviceAreas: company.service_areas || [],
        // 額外信息用於顯示
        services: this.getServicesFromIndustry(company.industry),
        // ✅ 使用动态计算的真实员工数量，而不是数据库中的静态值
        teamSize: companyEmployeesMap.get(company.id) || company.employee_count || 0,
        location: isUsableProviderLocation(company.location) ? company.location : undefined,
        email: email,
        profileImage: resolveProfileImageUrl(
          company.profile_image || company.users?.profile_image || undefined,
          { fallback: DEFAULT_COMPANY_IMAGE_PATH }
        ),
        preferredFlow: company.preferred_flow || null,
        // ✅ 新增：Premium 資訊
        isPremium: isPremium || false,
        premiumUntil: company.premium_until || undefined,
        premiumPlanType: premiumPlanType
        };
      }) || [];

      // ✅ 添加去重逻辑：相同公司名称只保留一个（优先保留 active 状态）
      const uniqueCompanies = companies.reduce((acc, company) => {
        // 查找是否已存在相同名称的公司（只根据公司名称判断，不考虑地址）
        const existingIndex = acc.findIndex(existing => 
          existing.companyName === company.companyName
        );
        
        if (existingIndex === -1) {
          // 不存在，直接添加
          acc.push(company);
        } else {
          // 已存在，比较优先级
          const existing = acc[existingIndex];
          
          // 优先级规则（按重要性排序）：
          // 1. active > pending（状态优先）
          // 2. 有公开头像 > 无头像（避免搜索卡片显示默认图标）
          // 3. 员工数量更多（优先保留真实活跃公司）
          // 4. 联系资料更完整（email / phone / address）
          // 5. 有评价 > 无评价（可信度）
          // 6. Premium > 非 Premium（付费用户优先）
          // 7. 创建时间更早的（更可能是原始记录）
          const getPriority = (c: Company): number => {
            let priority = 0;
            if (c.status === 'active') priority += 1000;
            if (c.profileImage && c.profileImage.trim()) priority += 500;
            if ((c.teamSize || 0) > 0) {
              // 上限 200，避免团队规模单一因素主导
              priority += Math.min((c.teamSize || 0) * 20, 200);
            }
            if (c.email && c.email.trim()) priority += 120;
            if (c.phone && c.phone.trim()) priority += 100;
            if (c.address && c.address.trim()) priority += 80;
            if (c.totalReviews > 0) priority += 50;
            if (c.isPremium) priority += 200;
            // 创建时间更早的优先级稍高（但影响较小）
            const ageScore = new Date(c.createdAt).getTime() / 1000000; // 转换为较小的数字
            priority += ageScore;
            return priority;
          };
          
          const companyPriority = getPriority(company);
          const existingPriority = getPriority(existing);
          const shouldReplace = companyPriority > existingPriority;
          
          if (shouldReplace) {
            console.log(`�� 替换重复公司: ${company.companyName} (保留 ID: ${company.id}, 移除 ID: ${existing.id})`);
            acc[existingIndex] = company;
          } else {
            console.log(`跳过重复公司: ${company.companyName} (ID: ${company.id}, 优先级: ${companyPriority.toFixed(2)} <= ${existingPriority.toFixed(2)})`);
          }
        }
        
        return acc;
      }, [] as Company[]);

      console.log(`✅ 去重前: ${companies.length} 个公司，去重后: ${uniqueCompanies.length} 个公司`);

      // ✅ 按 Premium 優先級排序（使用去重后的列表）
      const sortedCompanies = uniqueCompanies.sort((a, b) => {
        // 計算優先級分數
        const getPriorityScore = (company: Company): number => {
          if (!company.isPremium || !company.premiumPlanType) {
            return 0; // 非 Premium 公司
          }
          
          // 根據方案類型給予不同優先級
          switch (company.premiumPlanType) {
            case 'yearly':
              return 1000; // 年費方案：最高優先級 + 置頂
            case 'quarterly':
              return 500;  // 季費方案：高優先級
            case 'monthly':
              return 100;  // 月費方案：標準優先級
            default:
              return 0;
          }
        };
        
        const scoreA = getPriorityScore(a);
        const scoreB = getPriorityScore(b);
        
        // 先按 Premium 優先級排序
        if (scoreA !== scoreB) {
          return scoreB - scoreA;
        }
        
        // 相同優先級時，按評分排序
        return (b.rating || 0) - (a.rating || 0);
      });

      const publicCompanies = sortedCompanies.filter(
        (c) => !isLowQualityDisplayName(c.companyName) && isCompanyEligibleForHome(c)
      );

      return { companies: publicCompanies, total: publicCompanies.length };
    } catch (error: any) {
      console.error('獲取可用公司列表時發生錯誤:', error);
      // ✅ 檢查是否是網絡錯誤
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.message?.includes('ERR_NETWORK_ACCESS_DENIED')) {
        return { 
          companies: [], 
          total: 0,
          error: '網絡連接失敗，請檢查：\n1. 網絡連接是否正常\n2. 瀏覽器是否阻止了請求\n3. 防火牆設置是否正確'
        };
      }
      return { 
        companies: [], 
        total: 0,
        error: error?.message || '獲取公司列表時發生未知錯誤'
      };
    }
  }

  // 根據位置獲取附近的公司
  static async getNearbyCompanies(
    location: { lat: number; lng: number },
    radius: number = 10
  ): Promise<{
    companies: Company[];
    total: number;
  }> {
    try {
      console.log('獲取附近公司，位置:', location, '半徑:', radius);

      if (!isUsableUserGeoLocation(location)) {
        return { companies: [], total: 0 };
      }

      // 這裡可以實現基於地理位置的查詢
      // 目前先返回所有公司
      return await this.getAvailableCompanies();
    } catch (error) {
      console.error('獲取附近公司時發生錯誤:', error);
      return { companies: [], total: 0 };
    }
  }

  // 根據服務類型獲取公司
  static async getCompaniesByService(service: string): Promise<{
    companies: Company[];
    total: number;
  }> {
    try {
      console.log('根據服務類型獲取公司:', service);

      const { data: companiesData, error } = await supabase
        .from('companies')
        .select(`
          *,
          users!companies_user_id_fkey(id, name, email, phone, profile_image)
        `)
        .eq('status', 'active')
        .ilike('industry', `%${service}%`)
        .order('rating', { ascending: false });

      if (error) {
        console.error('根據服務類型獲取公司失敗:', error);
        return { companies: [], total: 0 };
      }

      const companies = companiesData?.map(company => ({
        id: company.id,
        userId: company.user_id,
        companyName: company.company_name,
        taxId: normalizeCompanyTaxIdForDisplay(company.tax_id),
        businessLicense: company.business_license?.trim() || '',
        description: company.description || '專業維修服務公司',
        address: company.address,
        phone: company.phone || company.users?.phone,
        website: company.website,
        employeeCount: company.employee_count || 0,
        establishedDate: company.established_date,
        industry: company.industry || '維修服務',
        companySize: company.company_size || '中小型',
        annualRevenue: company.annual_revenue || 0,
        status: company.status,
        rating: company.rating || 4.5,
        totalReviews: company.total_reviews || 0,
        totalEarnings: company.total_earnings || 0,
        completedJobs: company.completed_jobs || 0,
        isVerified: company.is_verified || false,
        createdAt: company.created_at,
        updatedAt: company.updated_at,
        services: this.getServicesFromIndustry(company.industry),
        teamSize: company.employee_count || 5,
        location: isUsableProviderLocation(company.location) ? company.location : undefined,
        email: company.users?.email || '',
        profileImage: resolveProfileImageUrl(
          company.profile_image || company.users?.profile_image || undefined,
          { fallback: DEFAULT_COMPANY_IMAGE_PATH }
        )
      })) || [];

      return { companies, total: companies.length };
    } catch (error) {
      console.error('根據服務類型獲取公司時發生錯誤:', error);
      return { companies: [], total: 0 };
    }
  }

  // 根據行業獲取服務列表
  private static getServicesFromIndustry(industry: string): string[] {
    const serviceMap: { [key: string]: string[] } = {
      '水電維修': ['水電', '管道', '電器'],
      '冷氣維修': ['冷氣', '空調', '製冷'],
      '家電維修': ['家電', '電器', '設備'],
      '裝修工程': ['裝修', '工程', '施工'],
      '緊急維修': ['緊急維修', '24小時服務'],
      '維修服務': ['水電', '冷氣', '家電', '裝修', '緊急維修']
    };

    return serviceMap[industry] || ['維修服務'];
  }

  // 獲取公司統計數據
  static async getCompanyStats(companyId: string): Promise<{
    totalEarnings: number;
    totalJobs: number;
    completedJobs: number;
    averageRating: number;
    totalReviews: number;
    accountBalance?: number;
  }> {
    try {
      console.log('📊 開始動態計算公司統計數據，公司ID:', companyId);

      // 1. 動態計算總收入（從 payments 表）
      const { data: paymentsData, error: paymentsError } = await supabase
        .from('payments')
        .select('amount, paid_amount, deposit_amount, remaining_amount, status')
        .eq('company_id', companyId)
        .in('status', ['completed', 'paid', 'approved']);

      let totalEarnings = 0;
      if (!paymentsError && paymentsData) {
        totalEarnings = paymentsData.reduce((sum, payment) => {
          // 優先使用實際已付金額（paid_amount）
          if (payment.paid_amount != null && payment.paid_amount > 0) {
            return sum + Number(payment.paid_amount);
          }
          // 如果狀態是 approved 但 paid_amount 為 null，使用 deposit_amount
          if (payment.status === 'approved' && payment.deposit_amount > 0) {
            return sum + Number(payment.deposit_amount);
          }
          // 最後使用 amount
          return sum + Number(payment.amount || 0);
        }, 0);
      }
      console.log('💰 總收入（動態計算）:', totalEarnings);

      // 2. 動態計算完成案件數（從 service_requests 表）
      const { data: completedJobsData, error: jobsError } = await supabase
        .from('service_requests')
        .select('id')
        .eq('company_id', companyId)
        .eq('status', 'completed');

      const completedJobs = completedJobsData?.length || 0;
      console.log('✅ 完成案件數（動態計算）:', completedJobs);

      // 3. 動態計算總案件數
      const { data: allJobsData } = await supabase
        .from('service_requests')
        .select('id')
        .eq('company_id', companyId);

      const totalJobs = allJobsData?.length || 0;
      console.log('📋 總案件數（動態計算）:', totalJobs);

      // 4. 動態計算評分和評價數（從 reviews 表）
      const { data: reviewsData, error: reviewsError } = await supabase
        .from('reviews')
        .select('rating')
        .eq('company_id', companyId);

      const totalReviews = reviewsData?.length || 0;
      const averageRating = totalReviews > 0 && reviewsData
        ? reviewsData.reduce((sum, review) => sum + (review.rating || 0), 0) / totalReviews
        : 0;
      console.log('⭐ 評價數（動態計算）:', totalReviews);
      console.log('⭐ 平均評分（動態計算）:', Math.round(averageRating * 10) / 10);

      // 5. 計算帳戶餘額（已完成的付款總額）
      const accountBalance = totalEarnings;

      return {
        totalEarnings: Math.round(totalEarnings * 100) / 100, // 保留兩位小數
        totalJobs,
        completedJobs,
        averageRating: Math.round(averageRating * 10) / 10, // 保留一位小數
        totalReviews,
        accountBalance: Math.round(accountBalance * 100) / 100
      };
    } catch (error) {
      console.error('獲取公司統計數據時發生錯誤:', error);
      return {
        totalEarnings: 0,
        totalJobs: 0,
        completedJobs: 0,
        averageRating: 0,
        totalReviews: 0,
        accountBalance: 0
      };
    }
  }

  // 計算過去一年的營業額（年營業額）
  static async calculateAnnualRevenue(companyId: string): Promise<number> {
    try {
      console.log('📊 開始計算年營業額，公司ID:', companyId);

      // 計算過去一年的時間範圍
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

      // 從 payments 表獲取過去一年的付款記錄
      const { data: paymentsData, error: paymentsError } = await supabase
        .from('payments')
        .select('amount, paid_amount, deposit_amount, remaining_amount, status, created_at')
        .eq('company_id', companyId)
        .in('status', ['completed', 'paid', 'approved'])
        .gte('created_at', oneYearAgo.toISOString());

      let annualRevenue = 0;
      if (!paymentsError && paymentsData) {
        annualRevenue = paymentsData.reduce((sum, payment) => {
          // 優先使用實際已付金額（paid_amount）
          if (payment.paid_amount != null && payment.paid_amount > 0) {
            return sum + Number(payment.paid_amount);
          }
          // 如果狀態是 approved 但 paid_amount 為 null，使用 deposit_amount
          if (payment.status === 'approved' && payment.deposit_amount > 0) {
            return sum + Number(payment.deposit_amount);
          }
          // 最後使用 amount
          return sum + Number(payment.amount || 0);
        }, 0);
      }
      console.log('💰 年營業額（過去一年）:', annualRevenue);

      return Math.round(annualRevenue * 100) / 100; // 保留兩位小數
    } catch (error) {
      console.error('計算年營業額時發生錯誤:', error);
      return 0;
    }
  }

  // 獲取公司各服務類別的統計數據
  static async getCompanyServiceStats(companyId: string): Promise<Record<number, {
    completedJobs: number;
    averageRating: number;
    totalReviews: number;
  }>> {
    try {
      console.log('📊 開始獲取公司服務統計數據，公司ID:', companyId);
      
      // 獲取該公司的所有服務請求（包括所有狀態，不只是 completed）
      const { data: allRequests, error: allRequestsError } = await supabase
        .from('service_requests')
        .select('id, category_id, status')
        .eq('company_id', companyId);

      if (allRequestsError) {
        console.error('❌ 獲取服務請求失敗:', allRequestsError);
        throw allRequestsError;
      }

      console.log('✅ 獲取到所有服務請求:', allRequests?.length || 0, '個');
      if (allRequests && allRequests.length > 0) {
        const statusCounts: Record<string, number> = {};
        allRequests.forEach((req: any) => {
          statusCounts[req.status] = (statusCounts[req.status] || 0) + 1;
        });
        console.log('📋 服務請求狀態分布:', statusCounts);
      }

      // 獲取該公司的所有已完成服務請求
      const completedRequests = allRequests?.filter((req: any) => req.status === 'completed') || [];
      console.log('✅ 獲取到已完成服務請求:', completedRequests.length, '個');

      // 獲取該公司的所有評價
      const { data: companyReviews, error: reviewsError } = await supabase
        .from('reviews')
        .select('id, rating, service_request_id, created_at')
        .eq('company_id', companyId);

      if (reviewsError) {
        console.error('❌ 獲取評價失敗:', reviewsError);
        throw reviewsError;
      }

      console.log('✅ 獲取到評價:', companyReviews?.length || 0, '個');
      if (companyReviews && companyReviews.length > 0) {
        const ratings = companyReviews.map((r: any) => r.rating).filter(Boolean);
        const avgRating = ratings.length > 0 
          ? ratings.reduce((sum, r) => sum + r, 0) / ratings.length 
          : 0;
        console.log('⭐ 評價詳情:', {
          總數: companyReviews.length,
          有評分的: ratings.length,
          平均評分: Math.round(avgRating * 10) / 10,
          評分分布: ratings.reduce((acc: Record<number, number>, r: number) => {
            acc[r] = (acc[r] || 0) + 1;
            return acc;
          }, {})
        });
      }

      // 獲取服務請求的類別信息
      const categoryStats: Record<number, {
        completedJobs: number;
        ratings: number[];
        reviewIds: Set<string>;
      }> = {};

      // 統計每個類別的完成案件數（只統計 completed 狀態）
      completedRequests.forEach((request: any) => {
        const categoryId = request.category_id;
        if (!categoryId) {
          console.warn('⚠️ 發現沒有 category_id 的服務請求:', request.id);
          return;
        }
        if (!categoryStats[categoryId]) {
          categoryStats[categoryId] = {
            completedJobs: 0,
            ratings: [],
            reviewIds: new Set()
          };
        }
        categoryStats[categoryId].completedJobs++;
      });

      console.log('📊 按類別統計完成案件數:', 
        Object.keys(categoryStats).map(catId => ({
          類別ID: catId,
          完成案件: categoryStats[parseInt(catId)].completedJobs
        }))
      );

      // 統計每個類別的評分
      if (companyReviews && companyReviews.length > 0) {
        // 使用之前獲取的 allRequests，不需要重新查詢
        const requestCategoryMap: Record<string, number> = {};
        allRequests?.forEach((req: any) => {
          if (req.id && req.category_id) {
            requestCategoryMap[req.id] = req.category_id;
          }
        });

        console.log('🔗 服務請求類別映射:', Object.keys(requestCategoryMap).length, '個請求有類別信息');

        let matchedReviews = 0;
        let unmatchedReviews = 0;

        companyReviews.forEach((review: any) => {
          const categoryId = requestCategoryMap[review.service_request_id];
          if (categoryId) {
            matchedReviews++;
            if (!categoryStats[categoryId]) {
              categoryStats[categoryId] = {
                completedJobs: 0,
                ratings: [],
                reviewIds: new Set()
              };
            }
            if (review.rating) {
              categoryStats[categoryId].ratings.push(review.rating);
            }
            categoryStats[categoryId].reviewIds.add(review.service_request_id);
          } else {
            unmatchedReviews++;
            console.warn('⚠️ 評價關聯的服務請求不存在或沒有類別:', {
              評價ID: review.id,
              服務請求ID: review.service_request_id
            });
          }
        });

        console.log('📝 評價匹配結果:', {
          總評價數: companyReviews.length,
          成功匹配: matchedReviews,
          未匹配: unmatchedReviews
        });
      }

      // 計算平均評分和總評價數
      const result: Record<number, {
        completedJobs: number;
        averageRating: number;
        totalReviews: number;
      }> = {};

      Object.keys(categoryStats).forEach((categoryIdStr) => {
        const categoryId = parseInt(categoryIdStr);
        const stats = categoryStats[categoryId];
        const avgRating = stats.ratings.length > 0
          ? stats.ratings.reduce((sum, r) => sum + r, 0) / stats.ratings.length
          : 0;

        result[categoryId] = {
          completedJobs: stats.completedJobs,
          averageRating: Math.round(avgRating * 10) / 10,
          totalReviews: stats.reviewIds.size
        };

        console.log(`📈 服務類別 ${categoryId} 統計:`, {
          完成案件: stats.completedJobs,
          平均評分: Math.round(avgRating * 10) / 10,
          總評價數: stats.reviewIds.size
        });
      });

      console.log('✅ 服務統計數據獲取完成，共', Object.keys(result).length, '個服務類別');
      return result;
    } catch (error: any) {
      console.error('獲取公司服務統計失敗:', error);
      return {};
    }
  }

  /**
   * 升級為高級會員
   */
  static async upgradeToPremium(
    companyId: string,
    planType: 'monthly' | 'quarterly' | 'yearly',
    paymentTransactionId: string,
    autoRenew: boolean = false
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const duration = getPremiumPlanDurationDays(planType);
      const planPrice = getPremiumPlanPrice(planType);
      
      // 計算到期時間
      const now = new Date();
      let premiumUntil = new Date(now);
      premiumUntil.setDate(premiumUntil.getDate() + duration);

      // 如果已經是 Premium 會員，延長到期時間
      const currentCompany = await this.getCompanyById(companyId);
      if (currentCompany?.isPremium && currentCompany.premiumUntil) {
        const currentExpiry = new Date(currentCompany.premiumUntil);
        if (currentExpiry > now) {
          // 從當前到期時間延長
          premiumUntil = new Date(currentExpiry);
          premiumUntil.setDate(premiumUntil.getDate() + duration);
        }
      }

      // 更新公司資料
      const { error } = await supabase
        .from('companies')
        .update({
          is_premium: true,
          premium_until: premiumUntil.toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', companyId);

      if (error) {
        console.error('升級 Premium 失敗:', error);
        return { success: false, error: error.message };
      }

      // 記錄訂閱歷史（如果 premium_subscriptions 表存在）
      try {
        console.log('📝 开始创建订阅记录:', {
          companyId,
          planType,
          price: planType === 'monthly' ? 999 : planType === 'quarterly' ? 2499 : 8999,
          expiresAt: premiumUntil.toISOString()
        });
        
        const { data: subscriptionData, error: subscriptionError } = await supabase
          .from('premium_subscriptions')
          .insert({
            company_id: companyId,
            plan_type: planType,
            price: planType === 'monthly' ? 999 : planType === 'quarterly' ? 2499 : 8999,
            started_at: now.toISOString(),
            expires_at: premiumUntil.toISOString(),
            payment_transaction_id: paymentTransactionId,
            status: 'active',
            auto_renew: autoRenew
          })
          .select()
          .single();

        if (subscriptionError) {
          console.error('❌ 記錄訂閱歷史失敗:', subscriptionError);
          console.error('错误代码:', subscriptionError.code);
          console.error('错误消息:', subscriptionError.message);
          console.error('错误详情:', subscriptionError.details);
          
          if (subscriptionError.code === '42501') {
            console.error('🔒 RLS 策略錯誤：請檢查 INSERT 策略是否正確設置');
          }
        } else {
          console.log('✅ 訂閱記錄創建成功:', subscriptionData);
          
          // ✅ 驗證功能觸發：記錄升級成功後的關鍵信息
          console.log('🎉 Premium 升級成功！功能觸發驗證:');
          console.log('  - 公司 ID:', companyId);
          console.log('  - 方案類型:', planType);
          console.log('  - 到期時間:', premiumUntil.toISOString());
          console.log('  - 自動續費:', autoRenew);
          console.log('  - 預期佣金率:', 
            planType === 'monthly' ? '10%' : 
            planType === 'quarterly' ? '9%' : '8%');
          console.log('  - 預期搜索優先級:', 
            planType === 'monthly' ? '標準優先級' : 
            planType === 'quarterly' ? '高優先級' : '最高優先級 + 置頂');
        }
      } catch (subscriptionError) {
        console.error('❌ 記錄訂閱歷史時發生錯誤:', subscriptionError);
      }

      return { success: true };
    } catch (error) {
      console.error('升級 Premium 時發生錯誤:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : '升級過程中發生錯誤' 
      };
    }
  }

  /**
   * 修復缺失的訂閱記錄
   * 為已經升級但沒有訂閱記錄的公司創建記錄
   */
  static async fixMissingSubscriptionRecord(companyId: string): Promise<{ success: boolean; error?: string; created?: boolean }> {
    try {
      // 1. 獲取公司資料
      const company = await this.getCompanyById(companyId);
      if (!company) {
        return { success: false, error: '找不到公司資料' };
      }

      // 2. 檢查是否為有效 Premium（與 Guard 相同：is_premium + premium_until > now）
      if (!isCompanyPremiumActive(company)) {
        return { success: false, error: '公司不是有效 Premium 會員' };
      }

      // 3. 檢查是否已有訂閱記錄
      const existingPlanType = await this.getCurrentPremiumPlanType(companyId);
      if (existingPlanType) {
        console.log('✅ 公司已有訂閱記錄，方案類型:', existingPlanType);
        return { success: true, created: false };
      }

      // 4. 根據到期時間推斷方案類型
      const premiumUntil = new Date(company.premiumUntil);
      const now = new Date();
      const daysRemaining = Math.ceil((premiumUntil.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      
      const planType = inferPremiumPlanFromDaysRemaining(daysRemaining);
      const price = getPremiumPlanPrice(planType);
      let startedAt = new Date(premiumUntil);
      startedAt.setDate(startedAt.getDate() - getPremiumPlanDurationDays(planType));

      // 確保開始時間不早於現在
      if (startedAt < now) {
        startedAt = now;
      }

      console.log('🔧 開始修復訂閱記錄:', {
        companyId,
        planType,
        price,
        startedAt: startedAt.toISOString(),
        expiresAt: premiumUntil.toISOString(),
        daysRemaining
      });

      // 5. 創建訂閱記錄
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .from('premium_subscriptions')
        .insert({
          company_id: companyId,
          plan_type: planType,
          price: price,
          started_at: startedAt.toISOString(),
          expires_at: premiumUntil.toISOString(),
          payment_transaction_id: `FIX_${Date.now()}`,
          status: 'active',
          auto_renew: false
        })
        .select()
        .single();

      if (subscriptionError) {
        console.error('❌ 修復訂閱記錄失敗:', subscriptionError);
        return { 
          success: false, 
          error: `創建訂閱記錄失敗: ${subscriptionError.message}` 
        };
      }

      console.log('✅ 訂閱記錄修復成功:', subscriptionData);
      return { success: true, created: true };
    } catch (error) {
      console.error('修復訂閱記錄時發生錯誤:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : '修復過程中發生錯誤' 
      };
    }
  }
}